import { useEffect, useRef, useState } from 'react';
import monaTentacle1 from '../../levels/mona/obstacles/m-1.svg';
import monaTentacle2 from '../../levels/mona/obstacles/m-2.svg';
import monaTentacle3 from '../../levels/mona/obstacles/m-3.svg';
import {
  MONA_TENTACLE_HOLD_MS,
  MONA_TENTACLE_SLIDE_MS,
} from './gameplayConstants';
import './LevelMonaTentacleOverlay.css';

type MonaTentaclePhase = 'in' | 'hold' | 'out';

const PHASE_MS: Record<MonaTentaclePhase, number> = {
  in: MONA_TENTACLE_SLIDE_MS,
  hold: MONA_TENTACLE_HOLD_MS,
  out: MONA_TENTACLE_SLIDE_MS,
};

const NEXT_PHASE: Record<MonaTentaclePhase, MonaTentaclePhase | null> = {
  in: 'hold',
  hold: 'out',
  out: null,
};

/** Back → front order from mod.svg groups m-3, m-2, m-1. */
const MONA_TENTACLES = [
  { id: 'm-3', src: monaTentacle3, sway: 'a' },
  { id: 'm-2', src: monaTentacle2, sway: 'b' },
  { id: 'm-1', src: monaTentacle1, sway: 'c' },
] as const;

type LevelMonaTentacleOverlayProps = {
  isPaused: boolean;
  onComplete: () => void;
};

export function LevelMonaTentacleOverlay({
  isPaused,
  onComplete,
}: LevelMonaTentacleOverlayProps) {
  const [phase, setPhase] = useState<MonaTentaclePhase>('in');
  const [raised, setRaised] = useState(false);
  const remainingMsRef = useRef(PHASE_MS.in);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    const frameId = window.requestAnimationFrame(() => {
      setRaised(true);
    });
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, []);

  useEffect(() => {
    if (phase === 'out') {
      setRaised(false);
    }
  }, [phase]);

  useEffect(() => {
    if (isPaused) {
      return;
    }
    let frameId = 0;
    let lastNow = performance.now();
    const tick = (now: number) => {
      remainingMsRef.current -= now - lastNow;
      lastNow = now;
      if (remainingMsRef.current > 0) {
        frameId = window.requestAnimationFrame(tick);
        return;
      }
      const next = NEXT_PHASE[phase];
      if (next == null) {
        onCompleteRef.current();
        return;
      }
      remainingMsRef.current = PHASE_MS[next];
      setPhase(next);
    };
    frameId = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [phase, isPaused]);

  return (
    <div className="level-mona-tentacle-overlay" aria-hidden>
      {MONA_TENTACLES.map((tentacle) => {
        const artClassName = [
          'level-mona-tentacle-overlay__art',
          raised ? 'level-mona-tentacle-overlay__art--raised' : '',
          phase === 'hold'
            ? `level-mona-tentacle-overlay__art--sway-${tentacle.sway}`
            : '',
          phase === 'hold' && isPaused
            ? 'level-mona-tentacle-overlay__art--sway-paused'
            : '',
        ]
          .filter(Boolean)
          .join(' ');

        return (
          <img
            key={tentacle.id}
            className={artClassName}
            style={{ transitionDuration: `${MONA_TENTACLE_SLIDE_MS}ms` }}
            src={tentacle.src}
            alt=""
          />
        );
      })}
    </div>
  );
}
